import { ChevronDown, ChevronRight } from 'lucide-react';
import { fmtCHF, fmtCHF2 } from '../utils/format';
import { RetrocessionProgramme } from '../types';

/**
 * Carte de suivi rétrocession / sur-utilisation d'une convention mesures.
 * Affichée dans le module Mesures (les décomptes s'y pilotent) ; une
 * rétrocession positive est par ailleurs imputée en déduction sur la
 * prévision de décembre de la grille Revenus.
 */
export function CarteRetrocession({ retro, depliee, onBasculer }: {
  retro: RetrocessionProgramme;
  depliee: boolean;
  onBasculer: () => void;
}) {
  const statut = retro.risque > 0.5 ? 'risque' : 'ok';
  const arrondi = (v: number) => Math.round(v * 100) / 100;
  return (
    <div className={`retro-card retro-card--${statut}`}>
      <div className="retro-card__entete" onClick={onBasculer}>
        {depliee ? <ChevronDown className="icon-sm" /> : <ChevronRight className="icon-sm" />}
        <span className="font-semi">{retro.contratLibelle}</span>
        {retro.tagTerme === 'projete' && (
          <span className="badge badge-warn badge-sm" title="Terme de convention projeté (hypothèse de scénario, pas encore signé)">
            terme projeté
          </span>
        )}
        {retro.tagOccupation == null && (
          <span className="badge badge-sm" title="Aucun décompte réel saisi — les 12 mois sont prévus aux places conventionnées">
            prévision = places conventionnées
          </span>
        )}
        {statut === 'ok' && <span className="badge badge-ok badge-sm">aucune rétrocession en vue</span>}
        {statut === 'risque' && (
          <span className="badge badge-err badge-sm">rétrocession estimée : CHF {fmtCHF(retro.risque)}</span>
        )}
        {retro.moisFactures > 0 && retro.moisFactures < 12 && (
          <span
            className={`badge badge-sm ${retro.rythmeMaxRestant < retro.rythmeActuel ? 'badge-warn' : 'badge-ok'}`}
            title={`${arrondi(retro.moisPlacesFacturees)} mois-places facturés sur ${retro.moisFactures} mois `
              + `(${arrondi(retro.rythmeActuel)}/mois). Il reste ${arrondi(retro.moisPlacesRestantes)} mois-places `
              + `à consommer sur ${12 - retro.moisFactures} mois : au-delà, la convention ne paie plus.`}
          >
            reste {arrondi(retro.moisPlacesRestantes)} mois-places → max {arrondi(retro.rythmeMaxRestant)}/mois
          </span>
        )}
        {retro.surUtilisation > 0 && (
          <span className="badge badge-warn badge-sm"
            title="Mesures au-delà de la convention : pas de revenu supplémentaire, mais information clé pour piloter les mesures individualisées">
            sur-utilisation : +{arrondi(retro.surUtilisation)} mois-places
          </span>
        )}
        <span className="text-xs text-ink-3" style={{ marginLeft: 'auto' }}>
          {arrondi(retro.moisPlacesRemplies)} / {retro.moisPlacesConvention} mois-places
          {' · '}plafond CHF {fmtCHF(retro.plafond)}
        </span>
      </div>
      {depliee && (
        <div className="retro-card__detail">
          <table className="table table-compact" style={{ maxWidth: 560 }}>
            <tbody>
              <tr><td>Terme {retro.annee} ({retro.places} places × CHF {fmtCHF2(retro.prixMensuel)}/pl./mois{retro.tagTerme === 'projete' ? ', projeté' : ''})</td>
                <td className="text-right tabular-nums">CHF {fmtCHF(retro.plafond)} /an</td></tr>
              <tr><td>Mois-places conventionnés ({retro.places} × 12)</td>
                <td className="text-right tabular-nums">{retro.moisPlacesConvention}</td></tr>
              <tr><td>Mois-places remplis (réels{retro.tagOccupation === 'projete' ? ' + projections' : ''} ; mois sans décompte prévus aux places)</td>
                <td className="text-right tabular-nums">{arrondi(retro.moisPlacesRemplies)}</td></tr>
              <tr><td>Manque ({arrondi(retro.manqueMoisPlaces)} mois-places × CHF {fmtCHF2(retro.prixMensuel)})</td>
                <td className="text-right tabular-nums">CHF {fmtCHF(retro.manque)}</td></tr>
              <tr><td>Facturé individualisé (même programme)</td>
                <td className="text-right tabular-nums">CHF {fmtCHF(retro.factureIndividualise)}</td></tr>
              <tr className="font-semi"><td>Rétrocession estimée (min du manque et de l'individualisé)</td>
                <td className="text-right tabular-nums">CHF {fmtCHF(retro.risque)}</td></tr>
              {retro.imputationDecembre > 0 && (
                <tr><td>Imputation dans Revenus (déduite de la prévision de décembre)</td>
                  <td className="text-right tabular-nums">− CHF {fmtCHF(retro.imputationDecembre)}</td></tr>
              )}
              {retro.moisFactures > 0 && retro.moisFactures < 12 && (
          <span
            className={`badge badge-sm ${retro.rythmeMaxRestant < retro.rythmeActuel ? 'badge-warn' : 'badge-ok'}`}
            title={`${arrondi(retro.moisPlacesFacturees)} mois-places facturés sur ${retro.moisFactures} mois `
              + `(${arrondi(retro.rythmeActuel)}/mois). Il reste ${arrondi(retro.moisPlacesRestantes)} mois-places `
              + `à consommer sur ${12 - retro.moisFactures} mois : au-delà, la convention ne paie plus.`}
          >
            reste {arrondi(retro.moisPlacesRestantes)} mois-places → max {arrondi(retro.rythmeMaxRestant)}/mois
          </span>
        )}
        {retro.surUtilisation > 0 && (
                <tr><td>Sur-utilisation (au-delà de la convention — non facturable)</td>
                  <td className="text-right tabular-nums">+{arrondi(retro.surUtilisation)} mois-places</td></tr>
              )}
            </tbody>
          </table>
          <p className="text-xs text-ink-3" style={{ marginBottom: 0 }}>
            La convention garantit son plafond (couverture de déficit). Il n'y a rétrocession que si les places
            conventionnées ont été sous-employées sur l'année ET que des mesures individualisées ont été vendues à la
            place : la DGCS reprend alors l'individualisé à hauteur du manque. Convention remplie ou sur-remplie :
            l'individualisé nous appartient. Une rétrocession positive est déduite de la prévision de décembre dans{' '}
            <a href="#revenus" className="text-accent">Revenus</a> ; le terme annuel se gère dans{' '}
            <a href="#contrats" className="text-accent">Contrats</a>.
          </p>
        </div>
      )}
    </div>
  );
}
